import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaPlus, FaCloudUploadAlt, FaTimes, FaArrowLeft, FaCalendar, FaTag, FaStar, FaEye, FaShare } from 'react-icons/fa';
import axios from 'axios';
import EnhancedRichTextEditor from '../EnhancedRichTextEditor';
import { API_ENDPOINTS } from '../../config/api';
import { getImageUrl, safeJsonParse } from '../../utils/imageUtils';

const WeeklyHighlightsForm = () => {
  console.log('WeeklyHighlightsForm component rendering...');
  
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [customUrlManuallyEdited, setCustomUrlManuallyEdited] = useState(false);
  const [issueDate, setIssueDate] = useState('');
  const [coveragePeriodStart, setCoveragePeriodStart] = useState('');
  const [coveragePeriodEnd, setCoveragePeriodEnd] = useState('');
  const [weeklyEdition, setWeeklyEdition] = useState('1st');
  const [coverageMonth, setCoverageMonth] = useState('');
  const [highlights, setHighlights] = useState([{ title: '', points: [''] }]);
  const [detailCards, setDetailCards] = useState([
    {
      id: 1,
      title: '',
      subtitle: '',
      content: '',
      referenceLinks: ['']
    }
  ]);
  
  // Debug detailCards state changes
  useEffect(() => {
    console.log('🔍 detailCards state changed:', detailCards);
  }, [detailCards]);
  const [fullDetail, setFullDetail] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [status, setStatus] = useState('draft');
  const [publishAt, setPublishAt] = useState('');
  const [tags, setTags] = useState([]);
  const [featured, setFeatured] = useState(false);
  
  const [coverImage, setCoverImage] = useState(null);
  const [socialImage, setSocialImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();
  const { identifier } = useParams();
  const auth = useAuth();
  const { token } = auth;
  
  const isEditMode = !!identifier;
  
  console.log('WeeklyHighlightsForm props:', {
    identifier,
    isEditMode,
    token: token ? 'Present' : 'Missing',
    auth: auth
  });
  
  // Debug component lifecycle
  useEffect(() => {
    console.log('WeeklyHighlightsForm mounted');
    console.log('🔍 Initial detailCards state:', detailCards);
    return () => {
      console.log('WeeklyHighlightsForm unmounted');
    };
  }, []);
  


  const handleBackClick = () => {
    navigate('/dashboard/weekly-highlights');
  };

  const generateCustomUrl = (title, startDate) => {
    if (!title) return '';
    
    // Generate base URL from title
    let baseUrl = title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
    
    // Add start date in YYYYMMDD format if available
    if (startDate) {
      const date = new Date(startDate);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}${month}${day}`;
      baseUrl = `${baseUrl}-${dateString}`;
    }
    
    return baseUrl;
  };

  useEffect(() => {
    // Generate custom URL when title or start date changes
    // In edit mode, only auto-generate if user hasn't manually edited the custom URL
    if (!isEditMode || !customUrlManuallyEdited) {
      setCustomUrl(generateCustomUrl(title, coveragePeriodStart));
    }
  }, [title, coveragePeriodStart, isEditMode, customUrlManuallyEdited]);

  // Additional effect to handle start date changes specifically
  useEffect(() => {
    // If start date changes and custom URL was manually edited, offer to regenerate
    if (customUrlManuallyEdited && isEditMode && coveragePeriodStart) {
      // Auto-regenerate custom URL when start date changes to maintain consistency
      setCustomUrl(generateCustomUrl(title, coveragePeriodStart));
      setCustomUrlManuallyEdited(false); // Reset the flag since we're regenerating
    }
  }, [coveragePeriodStart, customUrlManuallyEdited, isEditMode, title]);

  // Load existing highlight data when editing
  useEffect(() => {
    const loadHighlightData = async () => {
      if (isEditMode && identifier) {
        try {
          setLoading(true);

          const response = await axios.get(`${API_ENDPOINTS.WEEKLY_HIGHLIGHTS}/edit/${identifier}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const highlight = response.data;
          
          // Populate form fields with existing data
          setTitle(highlight.title || '');
          setSubtitle(highlight.subtitle || '');
          setCustomUrl(highlight.custom_url || '');
          setCustomUrlManuallyEdited(false); // Reset flag when loading existing data
          setIssueDate(highlight.issue_date ? highlight.issue_date.split('T')[0] : '');
          setCoveragePeriodStart(highlight.coverage_period_start ? highlight.coverage_period_start.split('T')[0] : '');
          setCoveragePeriodEnd(highlight.coverage_period_end ? highlight.coverage_period_end.split('T')[0] : '');
          setWeeklyEdition(highlight.weekly_edition || '1st');
          setCoverageMonth(highlight.coverage_month || '');
          // Handle highlights - parse JSON from database
          let parsedHighlights = [{ title: '', points: [''] }];
          
          if (highlight.highlights) {
            // Use safe parsing to handle both string and object data
            parsedHighlights = safeJsonParse(highlight.highlights, [{ title: '', points: [''] }]);
            
            // Ensure it's an array
            if (!Array.isArray(parsedHighlights)) {
              parsedHighlights = [{ title: '', points: [''] }];
            }
            
            // Ensure each highlight has the correct structure
            parsedHighlights = parsedHighlights.map(highlight => ({
              title: highlight.title || '',
              points: Array.isArray(highlight.points) ? highlight.points : ['']
            }));
          }
          
          setHighlights(parsedHighlights);

          // Handle detail cards - parse JSON string from backend
          let parsedCards;
          if (highlight.detail_cards) {
            // Use safe parsing to handle both string and object data
            parsedCards = safeJsonParse(highlight.detail_cards, []);
            
            // Ensure it's an array
            if (!Array.isArray(parsedCards)) {
              parsedCards = [];
            }
          } else {
            parsedCards = [];
          }
          
          // Ensure we have at least one empty card
          if (!parsedCards || parsedCards.length === 0) {
            parsedCards = [{ id: 1, title: '', subtitle: '', content: '', referenceLinks: [''] }];
          }

          setDetailCards(parsedCards);
          setFullDetail(highlight.full_detail || '');
          setMetaTitle(highlight.meta_title || '');
          setMetaDescription(highlight.meta_description || '');
          setStatus(highlight.status || 'draft');
          setPublishAt(highlight.publish_at ? highlight.publish_at.split('T')[0] : '');
          // Handle tags - safely parse JSON or provide default
          let parsedTags = safeJsonParse(highlight.tags, []);
          
          // Ensure it's an array
          if (!Array.isArray(parsedTags)) {
            parsedTags = [];
          }
          setTags(parsedTags);
          setFeatured(highlight.featured || false);
          
          // Handle existing images
          if (highlight.cover_image) {
            setCoverImage({ 
              file: null, 
              preview: getImageUrl(highlight.cover_image),
              isExisting: true 
            });
          }
          
          if (highlight.social_image) {
            setSocialImage({ 
              file: null, 
              preview: getImageUrl(highlight.social_image),
              isExisting: true 
            });
          }
          
          // Handle gallery images
          if (highlight.gallery_images && highlight.gallery_images.length > 0) {
            const galleryData = highlight.gallery_images.map(img => ({
              id: img.id,
              file: null,
              preview: getImageUrl(img.image_path),
              caption: img.caption || '',
              alt: img.alt_text || '',
              isExisting: true
            }));
            setGalleryImages(galleryData);
          }
          
        } catch (error) {
          console.error('Error loading highlight data:', error);
          setError('Failed to load highlight data');
        } finally {
          setLoading(false);
        }
      }
    };

    loadHighlightData();
  }, [isEditMode, identifier, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Temporary debugging
    console.log('🔍 Form submission - detailCards state:', detailCards);
    console.log('🔍 Form submission - detailCards length:', detailCards.length);
    console.log('🔍 Form submission - detailCards JSON:', JSON.stringify(detailCards));

    try {
      const formDataToSend = new FormData();
      
      formDataToSend.append('title', title);
      formDataToSend.append('subtitle', subtitle);
      formDataToSend.append('customUrl', customUrl);
      formDataToSend.append('issueDate', issueDate);
      formDataToSend.append('coveragePeriodStart', coveragePeriodStart);
      formDataToSend.append('coveragePeriodEnd', coveragePeriodEnd);
      formDataToSend.append('weeklyEdition', weeklyEdition);
      formDataToSend.append('coverageMonth', coverageMonth);
      formDataToSend.append('highlights', JSON.stringify(highlights));

      formDataToSend.append('detailCards', JSON.stringify(detailCards));
      formDataToSend.append('fullDetail', fullDetail);
      formDataToSend.append('metaTitle', metaTitle);
      formDataToSend.append('metaDescription', metaDescription);
      formDataToSend.append('status', status);
      formDataToSend.append('publishAt', publishAt);
      formDataToSend.append('tags', JSON.stringify(tags));
      formDataToSend.append('featured', featured);

      // Handle cover image
      if (coverImage && coverImage.file) {
        formDataToSend.append('coverImage', coverImage.file);
      }
      
      // Handle social image
      if (socialImage && socialImage.file) {
        formDataToSend.append('socialImage', socialImage.file);
      }
      
      // Handle gallery images
      galleryImages.forEach((image, index) => {
        if (image.file) {
          formDataToSend.append(`galleryImages`, image.file);
          formDataToSend.append(`galleryCaptions`, image.caption || '');
          formDataToSend.append(`galleryAltText`, image.alt || '');
        }
      });

      const url = isEditMode 
        ? `${API_ENDPOINTS.WEEKLY_HIGHLIGHTS}/edit/${identifier}`
        : API_ENDPOINTS.WEEKLY_HIGHLIGHTS;
      
      const method = isEditMode ? 'put' : 'post';
      

      
      const response = await axios({
        method,
        url,
        data: formDataToSend,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        navigate('/dashboard/weekly-highlights');
      }
    } catch (err) {
      console.error('Error saving highlight:', err);
      setError('Failed to save highlight: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const addHighlight = () => {
    setHighlights([...highlights, { title: '', points: [''] }]);
  };

  const removeHighlight = (index) => {
    if (highlights.length > 1) {
      const newHighlights = highlights.filter((_, i) => i !== index);
      setHighlights(newHighlights);
    }
  };

  const updateHighlightTitle = (index, title) => {
    const newHighlights = [...highlights];
    newHighlights[index].title = title;
    setHighlights(newHighlights);
  };

  const addHighlightPoint = (highlightIndex) => {
    const newHighlights = [...highlights];
    newHighlights[highlightIndex].points.push('');
    setHighlights(newHighlights);
  };

  const removeHighlightPoint = (highlightIndex, pointIndex) => {
    const newHighlights = [...highlights];
    if (newHighlights[highlightIndex].points.length > 1) {
      newHighlights[highlightIndex].points.splice(pointIndex, 1);
      setHighlights(newHighlights);
    }
  };

  const updateHighlightPoint = (highlightIndex, pointIndex, value) => {
    const newHighlights = [...highlights];
    newHighlights[highlightIndex].points[pointIndex] = value;
    setHighlights(newHighlights);
  };

  const addTag = () => {
    if (tags.length < 10) {
      setTags([...tags, '']);
    }
  };

  const removeTag = (index) => {
    const newTags = tags.filter((_, i) => i !== index);
    setTags(newTags);
  };

  const updateTag = (index, value) => {
    const newTags = [...tags];
    newTags[index] = value;
    setTags(newTags);
  };

  // Detail Cards handlers
  const addDetailCard = () => {
    console.log('🔍 addDetailCard called - current detailCards:', detailCards);
    const newCard = {
      id: Date.now(),
      title: '',
      subtitle: '',
      content: '',
      referenceLinks: ['']
    };
    console.log('🔍 addDetailCard - new card:', newCard);
    setDetailCards([...detailCards, newCard]);
    console.log('🔍 addDetailCard - updated detailCards:', [...detailCards, newCard]);
  };

  const removeDetailCard = (index) => {
    if (detailCards.length > 1) {
      const newCards = detailCards.filter((_, i) => i !== index);
      setDetailCards(newCards);
    }
  };

  const updateDetailCardTitle = (index, title) => {
    const newCards = [...detailCards];
    newCards[index].title = title;
    setDetailCards(newCards);
  };

  const updateDetailCardSubtitle = (index, subtitle) => {
    const newCards = [...detailCards];
    newCards[index].subtitle = subtitle;
    setDetailCards(newCards);
  };

  const updateDetailCardContent = (index, content) => {
    const newCards = [...detailCards];
    newCards[index].content = content;
    setDetailCards(newCards);
  };

  const addReferenceLink = (cardIndex) => {
    const newCards = [...detailCards];
    newCards[cardIndex].referenceLinks.push('');
    setDetailCards(newCards);
  };

  const removeReferenceLink = (cardIndex, linkIndex) => {
    const newCards = [...detailCards];
    if (newCards[cardIndex].referenceLinks.length > 1) {
      newCards[cardIndex].referenceLinks.splice(linkIndex, 1);
      setDetailCards(newCards);
    }
  };

  const updateReferenceLink = (cardIndex, linkIndex, value) => {
    const newCards = [...detailCards];
    newCards[cardIndex].referenceLinks[linkIndex] = value;
    setDetailCards(newCards);
  };

  // Image upload handlers
  const handleCoverImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImage({
        file: file,
        preview: URL.createObjectURL(file),
        isExisting: false
      });
    }
  };

  const handleSocialImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSocialImage({
        file: file,
        preview: URL.createObjectURL(file),
        isExisting: false
      });
    }
  };

  const handleGalleryImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newGalleryImages = files.map(file => ({
      file: file,
      preview: URL.createObjectURL(file),
      caption: '',
      alt: '',
      isExisting: false
    }));
    setGalleryImages([...galleryImages, ...newGalleryImages]);
  };

  const removeGalleryImage = (index) => {
    const newGalleryImages = galleryImages.filter((_, i) => i !== index);
    setGalleryImages(newGalleryImages);
  };

  const updateGalleryImageCaption = (index, caption) => {
    const newGalleryImages = [...galleryImages];
    newGalleryImages[index].caption = caption;
    setGalleryImages(newGalleryImages);
  };

  const updateGalleryImageAlt = (index, alt) => {
    const newGalleryImages = [...galleryImages];
    newGalleryImages[index].alt = alt;
    setGalleryImages(newGalleryImages);
  };

  // Check if auth is properly loaded (after all hooks)
  if (!auth || !token) {
    console.log('Auth not ready, showing loading...');
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading authentication...</p>
        </div>
      </div>
    );
  }

  try {
    console.log('Rendering WeeklyHighlightsForm JSX...');
    
    return (
      <div className="container mt-4">
        <div className="row">
          <div className="col-12">
            <div className="d-flex align-items-center mb-4">
              <button
                type="button"
                className="btn btn-outline-secondary me-3"
                onClick={handleBackClick}
              >
                <FaArrowLeft className="me-2" />
                Back to Highlights
              </button>
              <h2 className="mb-0">
                {isEditMode ? 'Edit Weekly Highlight' : 'Create New Weekly Highlight'}
              </h2>
            </div>

          <form onSubmit={handleSubmit} className="needs-validation" noValidate>
            {/* Basic Information */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">📝 Basic Information</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-8">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                          // If user is editing title and custom URL was manually edited, 
                          // offer to regenerate it
                          if (customUrlManuallyEdited && isEditMode) {
                            setCustomUrlManuallyEdited(false);
                          }
                          // Auto-update custom URL when title changes
                          if (!customUrlManuallyEdited || !isEditMode) {
                            setCustomUrl(generateCustomUrl(e.target.value, coveragePeriodStart));
                          }
                        }}
                        placeholder="Enter highlight title..."
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Issue Date *</label>
                      <input
                        type="date"
                        className="form-control"
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mb-3">
                  <label className="form-label fw-semibold">Subtitle</label>
                  <input
                    type="text"
                    className="form-control"
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    placeholder="Enter subtitle..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Custom URL</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={customUrl}
                      onChange={(e) => {
                        setCustomUrl(e.target.value);
                        setCustomUrlManuallyEdited(true);
                      }}
                      placeholder="auto-generated-url-YYYYMMDD"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setCustomUrl(generateCustomUrl(title, coveragePeriodStart));
                        setCustomUrlManuallyEdited(false);
                      }}
                      title="Regenerate from title and start date"
                    >
                      🔄
                    </button>
                  </div>
                  <div className="form-text">Leave empty to auto-generate from title and start date, or click the refresh button to regenerate</div>
                </div>
              </div>
            </div>

            {/* Coverage Period Section */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">📅 Coverage Period</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Coverage Period</label>
                  
                  {/* Date Range and Edition Selection */}
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={coveragePeriodStart}
                        onChange={(e) => {
                          setCoveragePeriodStart(e.target.value);
                          // Auto-update custom URL when start date changes
                          if (!customUrlManuallyEdited || !isEditMode) {
                            setCustomUrl(generateCustomUrl(title, e.target.value));
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={coveragePeriodEnd}
                        onChange={(e) => setCoveragePeriodEnd(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Weekly Edition</label>
                      <select
                        className="form-select"
                        value={weeklyEdition}
                        onChange={(e) => setWeeklyEdition(e.target.value)}
                      >
                        <option value="1st">1st Weekly Edition</option>
                        <option value="2nd">2nd Weekly Edition</option>
                        <option value="3rd">3rd Weekly Edition</option>
                        <option value="4th">4th Weekly Edition</option>
                        <option value="5th">5th Weekly Edition</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label fw-semibold">Month</label>
                      <select
                        className="form-select"
                        value={coverageMonth}
                        onChange={(e) => setCoverageMonth(e.target.value)}
                      >
                        <option value="">Select Month</option>
                        <option value="January">January</option>
                        <option value="February">February</option>
                        <option value="March">March</option>
                        <option value="April">April</option>
                        <option value="May">May</option>
                        <option value="June">June</option>
                        <option value="July">July</option>
                        <option value="August">August</option>
                        <option value="September">September</option>
                        <option value="October">October</option>
                        <option value="November">November</option>
                        <option value="December">December</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Highlights Section */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">⭐ Highlights of the Week</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label fw-semibold">Highlights of the Week</label>
                  {highlights && highlights.length > 0 ? highlights.map((highlight, highlightIndex) => (
                    <div key={highlightIndex} className="border rounded p-3 mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Section title"
                          value={highlight.title}
                          onChange={(e) => updateHighlightTitle(highlightIndex, e.target.value)}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={() => removeHighlight(highlightIndex)}
                          disabled={highlights.length === 1}
                        >
                          <FaTimes />
                        </button>
                      </div>
                      {highlight.points.map((point, pointIndex) => (
                        <div key={pointIndex} className="d-flex align-items-center mb-2">
                          <span className="me-2">•</span>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Highlight point"
                            value={point}
                            onChange={(e) => updateHighlightPoint(highlightIndex, pointIndex, e.target.value)}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => removeHighlightPoint(highlightIndex, pointIndex)}
                            disabled={highlight.points.length === 1}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => addHighlightPoint(highlightIndex)}
                      >
                        <FaPlus className="me-1" />
                        Add Point
                      </button>
                    </div>
                  )) : (
                    <div className="text-muted p-3 border rounded">
                      No highlights found. Click "Add Highlight Section" to create one.
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={addHighlight}
                  >
                    <FaPlus className="me-1" />
                    Add Highlight Section
                  </button>
                </div>
              </div>
            </div>

            {/* Full Detail Highlight Cards */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">📄 Full Detail Highlight</h5>
              </div>
              <div className="card-body">
                {detailCards.map((card, cardIndex) => (
                  <div key={card.id} className="border rounded p-4 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Card {cardIndex + 1}</h6>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeDetailCard(cardIndex)}
                        disabled={detailCards.length === 1}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    
                    {/* Card Title */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Card Title</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., 1. Economy at a glance - data release(s) of the week"
                        value={card.title}
                        onChange={(e) => updateDetailCardTitle(cardIndex, e.target.value)}
                      />
                    </div>
                    
                    {/* Card Subtitle */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Card Subtitle</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., 1.1. Services PMI at 11-month high in July on strong demand, including from abroad"
                        value={card.subtitle}
                        onChange={(e) => updateDetailCardSubtitle(cardIndex, e.target.value)}
                      />
                    </div>
                    
                    {/* Card Content */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Card Content</label>
                      <EnhancedRichTextEditor
                        value={card.content}
                        onChange={(content) => updateDetailCardContent(cardIndex, content)}
                        placeholder="Enter detailed content for this card..."
                        editorType="quill"
                        height="300px"
                      />
                    </div>
                    
                    {/* Reference Links */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Reference Links</label>
                      {card.referenceLinks.map((link, linkIndex) => (
                        <div key={linkIndex} className="d-flex align-items-center mb-2">
                          <input
                            type="url"
                            className="form-control"
                            placeholder="https://example.com/article"
                            value={link}
                            onChange={(e) => updateReferenceLink(cardIndex, linkIndex, e.target.value)}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger ms-2"
                            onClick={() => removeReferenceLink(cardIndex, linkIndex)}
                            disabled={card.referenceLinks.length === 1}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => addReferenceLink(cardIndex)}
                      >
                        <FaPlus className="me-1" />
                        Add Reference Link
                      </button>
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={addDetailCard}
                >
                  <FaPlus className="me-1" />
                  Add More Cards
                </button>
              </div>
            </div>

            {/* Images Section */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">🖼️ Images</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Cover Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleCoverImageUpload}
                      />
                      {coverImage && coverImage.preview && (
                        <div className="mt-2 position-relative d-inline-block">
                          <img 
                            src={coverImage.preview} 
                            alt="Cover preview" 
                            className="img-thumbnail" 
                            style={{ maxHeight: '150px' }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger position-absolute"
                            style={{ top: '5px', right: '5px' }}
                            onClick={() => setCoverImage(null)}
                            title="Remove image"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Social Media Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleSocialImageUpload}
                      />
                      {socialImage && socialImage.preview && (
                        <div className="mt-2 position-relative d-inline-block">
                          <img 
                            src={socialImage.preview} 
                            alt="Social image preview" 
                            className="img-thumbnail" 
                            style={{ maxHeight: '150px' }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger position-absolute"
                            style={{ top: '5px', right: '5px' }}
                            onClick={() => setSocialImage(null)}
                            title="Remove image"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Gallery Images</label>
                  <input
                    type="file"
                    className="form-control mb-2"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryImageUpload}
                  />
                  
                  {galleryImages.length > 0 && (
                    <div className="row">
                      {galleryImages.map((image, index) => (
                        <div key={index} className="col-md-4 mb-3">
                          <div className="card">
                            <div className="position-relative">
                              <img 
                                src={image.preview} 
                                alt="Gallery preview" 
                                className="card-img-top" 
                                style={{ height: '150px', objectFit: 'cover' }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger position-absolute"
                                style={{ top: '5px', right: '5px' }}
                                onClick={() => removeGalleryImage(index)}
                                title="Remove image"
                              >
                                <FaTimes />
                              </button>
                            </div>
                            <div className="card-body">
                              <input
                                type="text"
                                className="form-control form-control-sm mb-2"
                                placeholder="Caption"
                                value={image.caption}
                                onChange={(e) => updateGalleryImageCaption(index, e.target.value)}
                              />
                              <input
                                type="text"
                                className="form-control form-control-sm mb-2"
                                placeholder="Alt text"
                                value={image.alt}
                                onChange={(e) => updateGalleryImageAlt(index, e.target.value)}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removeGalleryImage(index)}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SEO Section */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">🔍 SEO</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Meta Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={metaTitle}
                        onChange={(e) => setMetaTitle(e.target.value)}
                        placeholder="Enter meta title for SEO..."
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Meta Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={metaDescription}
                        onChange={(e) => setMetaDescription(e.target.value)}
                        placeholder="Enter meta description for SEO..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Settings Section */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">⚙️ Settings</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Status</label>
                      <select
                        className="form-select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="draft">Draft</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label className="form-label fw-semibold">Publish Date</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={publishAt}
                        onChange={(e) => setPublishAt(e.target.value)}
                        disabled={status !== 'scheduled'}
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="mb-3">
                      <div className="form-check form-switch mt-4">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="featured"
                          checked={featured}
                          onChange={(e) => setFeatured(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="featured">
                          <FaStar className="text-warning me-1" />
                          Featured Highlight
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div className="mb-3">
                  <label className="form-label fw-semibold">Tags</label>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    {tags.map((tag, index) => (
                      <div key={index} className="d-flex align-items-center">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          style={{ width: '120px' }}
                          value={tag}
                          onChange={(e) => updateTag(index, e.target.value)}
                          placeholder="Tag"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger ms-1"
                          onClick={() => removeTag(index)}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                  {tags.length < 10 && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={addTag}
                    >
                      <FaPlus className="me-1" />
                      Add Tag
                    </button>
                  )}
                  <div className="form-text">Maximum 10 tags allowed</div>
                </div>
              </div>
            </div>

            {error && <div className="alert alert-danger">❌ {error}</div>}
            
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/dashboard/weekly-highlights')}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? '🔄 Saving...' : (isEditMode ? '💾 Update Highlight' : 'Create Highlight')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    );
  } catch (error) {
    console.error('Error rendering WeeklyHighlightsForm:', error);
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4>❌ Error Loading Form</h4>
          <p>There was an error loading the Weekly Highlight form. Please try refreshing the page.</p>
          <details>
            <summary>Error Details</summary>
            <pre>{error.message}</pre>
            <pre>{error.stack}</pre>
          </details>
        </div>
      </div>
    );
  }
};

export default WeeklyHighlightsForm; 

